// 261010 Epic 2 / P2b-1 — bước SƠ ĐỒ của một video, CÓ thao tác (khung mới). Tái dùng `PositionMapCard` (props độc lập) cho các setup gắn với giai đoạn của video;
// `PositionMapPanel` cả plan nằm trong ngăn kéo (thêm/sửa/xoá setup, gán liên kết). Quy tắc hiển thị/bật: `workflowActionPolicy` (server vẫn là nơi chặn thật).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Modal, Space, Tag, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlanScene, FilmCharacter, FilmCharacterLook, FilmEvent, FilmLocation, PositionMapsResponse, PositionMapWriteBody } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import PositionMapCard from './PositionMapCard';
import PositionMapPanel from './PositionMapPanel';
import { buildMatrix, filterVisibleFailures, isPositionJobType, proposeImpact, usageLabels, POSITION_JOB, type MatrixScene } from './positionMapUtils';
import { actionOf, actionsFor, mapCtxOf, runningKindsOf, workflowActionError, type ActionError, type WorkflowActionCtx } from './workflowActionPolicy';
import { RunningTag, StepErrorAlert, type InteractiveProps } from './WorkflowStepShared';
import { needsConfirm } from './workflowConfirmPolicy';

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  scenes: EventPlanScene[];
  maps: PositionMapsResponse | null;
  mapsError: string | null;
}

const WorkflowMapStep: React.FC<Props> = ({ planId, storyId, eventId, ev, scenes, maps, mapsError, stepState, jobs, failures, onActionDone, onJobsRefresh }) => {
  const alive = useAliveRef();
  const [characters, setCharacters] = useState<FilmCharacter[]>([]);
  const [looks, setLooks] = useState<FilmCharacterLook[]>([]);
  const [locations, setLocations] = useState<FilmLocation[]>([]);
  const [err, setErr] = useState<ActionError | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [dismissed, setDismissed] = useState<string[]>([]);

  // Nhân vật / look / bối cảnh (ô chọn trên thẻ) — đổi hiếm ⇒ nạp một lần khi mở bước.
  useEffect(() => {
    if (!storyId) return;
    Promise.all([filmApi.listCharacters(storyId), filmApi.listLooks(storyId), filmApi.listLocations(storyId)])
      .then(([c, l, loc]) => { if (alive.current) { setCharacters(c); setLooks(l); setLocations(loc); } })
      .catch(() => { /* thiếu danh sách thì chỉ mất ô chọn look; thẻ vẫn hiện tên rút gọn */ });
  }, [storyId, alive]);

  const phaseOrders = useMemo(() => (ev.videoMeta?.phases || []).map((p) => p.order), [ev]);
  const ctx: WorkflowActionCtx = useMemo(() => ({
    isSky: true, stepState, runningJobKinds: runningKindsOf(jobs, eventId), map: mapCtxOf(maps, eventId, phaseOrders, jobs),
  }), [stepState, jobs, eventId, maps, phaseOrders]);
  const actions = useMemo(() => actionsFor('MAP', ctx), [ctx]);
  const rows = useMemo(() => (maps ? buildMatrix(scenes as unknown as MatrixScene[], maps.links.valid, maps.links.drifted, maps.uncoveredStages) : []), [maps, scenes]);
  const myRows = rows.filter((r) => r.eventId === eventId);
  const setupIds = ctx.map?.setups.map((s) => s.id) ?? [];
  const cards = (maps?.maps || []).filter((m) => setupIds.includes(m.id));
  const setupsRunning = jobs.some((j) => j.type === POSITION_JOB.SETUPS);
  const runningMapIds = new Set(jobs.filter((j) => j.type === POSITION_JOB.MAP).map((j) => j.key));
  const visibleFailures = filterVisibleFailures(failures).filter((f) => isPositionJobType(f.type) && !dismissed.includes(f.id)).slice(0, 3);
  const propose = actionOf(actions, 'MAP_PROPOSE');
  const openPanel = actionOf(actions, 'MAP_OPEN_PANEL');

  /** Một thao tác ghi: lỗi nghiệp vụ hiện trong khối; DÙ THÀNH CÔNG HAY LỖI đều báo trang tải lại widget + workflow-status. */
  const run = useCallback(async (what: string, fn: () => Promise<unknown>): Promise<boolean> => {
    setErr(null);
    try { await fn(); return true; }
    catch (e: any) { if (alive.current) setErr(workflowActionError(e, what)); return false; }
    finally { onActionDone(); }
  }, [onActionDone, alive]);

  const doPropose = async () => {
    setProposing(true);
    await run('Đề xuất setup', async () => { const s = await filmApi.proposePositionMaps(planId); message.info('AI đang đề xuất setup ở nền (~1–2 phút).'); await onJobsRefresh(); return s; });
    if (alive.current) setProposing(false);
  };
  const askPropose = () => {
    const all = maps?.maps || [];
    if (!all.length || !needsConfirm('MAP', 'RERUN')) { void doPropose(); return; } // U9: đề xuất lại setup chạy ngay (phần đã sửa tay / có ảnh được máy giữ)
    const { replaced, kept } = proposeImpact(all);
    Modal.confirm({
      title: 'AI đề xuất lại setup?', width: 520, okText: 'Đề xuất lại', cancelText: 'Huỷ',
      content: (
        <div style={{ fontSize: 13 }}>
          <div style={{ marginBottom: 6 }}><b>Sẽ bị thay ({replaced.length})</b> — do AI viết và chưa có ảnh:</div>
          <div style={{ marginBottom: 8 }}>{replaced.length ? replaced.map((n) => <Tag key={n} color="orange">{n}</Tag>) : <i>không có</i>}</div>
          <div style={{ marginBottom: 6 }}><b>Được giữ ({kept.length})</b> — đã sửa (tay hoặc agent) hoặc đã có ảnh, cùng liên kết của chúng:</div>
          <div>{kept.length ? kept.map((n) => <Tag key={n} color="green">{n}</Tag>) : <i>không có</i>}</div>
        </div>
      ),
      onOk: doPropose,
    });
  };

  const patch = (id: string, body: PositionMapWriteBody) => run('Lưu setup', () => filmApi.patchPositionMap(id, body));
  const generate = (id: string, name: string) => run(`Tạo ảnh "${name}"`, async () => {
    await filmApi.generatePositionMap(id);
    message.info(`Đang tạo ảnh "${name}" ở nền.`);
    await onJobsRefresh();
  }).then(() => undefined);
  const approve = (id: string, name: string) => run(`Duyệt "${name}"`, () => filmApi.approvePositionMap(id)).then(() => undefined);
  const unapprove = (id: string, name: string) => run(`Bỏ duyệt "${name}"`, () => filmApi.unapprovePositionMap(id)).then(() => undefined);
  const remove = (id: string, name: string) => run(`Xoá "${name}"`, () => filmApi.deletePositionMap(id)).then(() => undefined);

  if (mapsError) return <Alert type="error" showIcon message={`Không tải được sơ đồ: ${mapsError}`} action={<Button size="small" onClick={onActionDone}>Tải lại</Button>} />;
  if (!maps) return <div className="muted" style={{ padding: 8 }}>Đang tải sơ đồ…</div>;

  return (
    <div data-testid="wf-map-step">
      <StepErrorAlert error={err} onReload={onActionDone} onClose={() => setErr(null)} />
      {visibleFailures.map((f) => (
        <Alert key={f.id} type="error" showIcon closable style={{ marginBottom: 8 }} onClose={() => setDismissed((d) => [...d, f.id])}
          message={`${f.type === POSITION_JOB.SETUPS ? 'Đề xuất setup' : 'Tạo ảnh sơ đồ'} thất bại — ${f.minutesAgo}′ trước`} description={<span style={{ fontSize: 12 }}>{f.note}</span>} />
      ))}
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        {propose?.visible && <Button size="small" onClick={askPropose} loading={proposing} disabled={!propose.enabled} title={propose.enabled ? undefined : propose.reason}>{propose.label}</Button>}
        {openPanel?.visible && <Button size="small" onClick={() => setPanelOpen(true)} disabled={!openPanel.enabled}>{openPanel.label}</Button>}
        {setupsRunning && <RunningTag>AI đang đề xuất setup…</RunningTag>}
        {runningMapIds.size > 0 && <RunningTag>đang tạo {runningMapIds.size} ảnh sơ đồ…</RunningTag>}
        <span className="muted" style={{ fontSize: 12 }}>{ctx.map!.linkedPhases}/{ctx.map!.totalPhases} giai đoạn đã có setup</span>
      </Space>

      {myRows.length > 0 && (
        <ul className="wf-list" style={{ marginBottom: 10 }} data-testid="wf-map-stages">
          {myRows.map((r) => (
            <li key={r.key}><span className="k">{r.phaseOrder === null ? 'Cả video' : `GĐ${r.phaseOrder}`}</span>
              {r.mapId ? <span>setup <b>{maps.maps.find((m) => m.id === r.mapId)?.name || '—'}</b></span>
                : r.drift ? <span style={{ color: '#a32a17' }}>liên kết bị lệch — mở "Quản lý sơ đồ cả plan" để gán lại</span>
                  : <span className="muted">chưa liên kết setup nào</span>}
            </li>
          ))}
        </ul>
      )}

      {cards.length === 0 ? (
        <p className="muted" data-testid="wf-map-empty">Video này chưa có setup sơ đồ nào được gán. Bấm "AI đề xuất setup" hoặc mở "Quản lý sơ đồ cả plan" để thêm và gán.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
          {cards.map((m) => (
            <PositionMapCard key={m.id} chipPrompt map={m} allMaps={maps.maps} characters={characters} looks={looks} locations={locations}
              usage={usageLabels(m.id, rows)} running={runningMapIds.has(m.id)} hideCost
              policy={{
                generate: actionOf(actions, 'MAP_GENERATE', m.id), approve: actionOf(actions, 'MAP_APPROVE', m.id), unapprove: actionOf(actions, 'MAP_UNAPPROVE', m.id),
              }}
              onPatch={(body) => patch(m.id, body)} onGenerate={() => generate(m.id, m.name)} onDelete={() => remove(m.id, m.name)}
              onApprove={() => approve(m.id, m.name)} onUnapprove={() => unapprove(m.id, m.name)} />
          ))}
        </div>
      )}

      <Drawer title="Sơ đồ định vị — cả plan" open={panelOpen} onClose={() => { setPanelOpen(false); onActionDone(); }} width="min(1100px, 100%)" destroyOnClose>
        <PositionMapPanel planId={planId} storyId={storyId} scenes={scenes as unknown as MatrixScene[]} jobs={jobs} failures={failures} onJobsRefresh={onJobsRefresh}
          hideCost alwaysOpen onActionDone={onActionDone} onActionError={(e) => setErr(e)} />
      </Drawer>
    </div>
  );
};

export default WorkflowMapStep;
